import React, { useState } from 'react'
import {
  Box,
  Button,
  Paper,
  TablePagination,
  Tooltip,
  useMediaQuery,
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { useTheme } from '@material-ui/styles'
import {
  FileText,
  HelpCircle as HelpCircleIcon,
  Mail as MailIcon,
  Send as SendIcon,
} from 'react-feather'

import ticketConstants from 'constants/tickets'
import technicalFaqsConstants from 'constants/technicalFaqs'

import {
  Container,
  ContentHeader,
  DriveTour,
  FatButton,
  FilterButton,
  Filters,
  LoadingFeedback,
  Page,
  ShadowCard,
} from 'components'

import helpers from 'helpers'

import useAuth from 'hooks/useAuth'
import useFetch from 'hooks/useFetch'
import useFilter from 'hooks/useFilter'
import usePagination from 'hooks/usePagination'

import useStyles from './styles'
import * as service from 'service'
import { TicketTable } from '../components'

const Tickets = () => {
  const [selectedItem, setSelectedItem] = useState('all')
  const [openTour, setOpenTour] = useState(false)

  const classes = useStyles()
  const filter = useFilter()
  const theme = useTheme()
  const { user } = useAuth()
  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(10)

  const { JURIDIC_TICKET_TYPE, SECURITY_TICKET_TYPE } =
    ticketConstants.TICKET_TYPE

  const isMine = selectedItem === 'mine'
  const isAll = selectedItem === 'all'

  const { response, isLoading, refresh } = useFetch(
    service.dponet.tickets.getAll,
    {
      perPage,
      page,
      ...{ ...filter.filters },
      ...(isMine && { userId: user.id }),
      ticket_types: [JURIDIC_TICKET_TYPE, SECURITY_TICKET_TYPE],
    },
    [page, perPage, filter.filters, selectedItem],
  )

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })

  const { mixpanel } = helpers

  const handleOpenTour = () => {
    setOpenTour(true)
    mixpanel.track('Apoio Tecnico Solicitacoes', 'Tour', {
      action: 'tour-minhas-solicitacoes-central-de-privacidade',
    })
  }

  return (
    <Page title="Solicitações">
      <LoadingFeedback open={isLoading} />
      <Container maxWidth={false}>
        <ContentHeader title="Solicitações">
          <Tooltip title="Iniciar o tour guiado">
            <Button
              variant="contained"
              color="primary"
              startIcon={<HelpCircleIcon size={20} />}
              onClick={handleOpenTour}
              fullWidth
              id={
                technicalFaqsConstants.TECHNICAL_FAQS_TICKETS_MAIN_DRIVER_STEP_1
              }
            >
              Tutorial
            </Button>
          </Tooltip>
          <FilterButton setDrawerOpen={filter.setDrawerOpen} />
        </ContentHeader>
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(4)}>
          <ShadowCard
            imageSrc={<FileText size={48} />}
            text="A plataforma disponibiliza serviços jurídicos especializados
                para auxiliar na adequação da sua organização à LGPD (Lei Geral
                de Proteção de Dados). Neste menu, oferecemos um atendimento
                jurídico altamente especializado, permitindo que você envie
                documentos para análise e receba pareceres jurídicos valiosos.
                Através dos nossos serviços, você poderá garantir a conformidade
                de sua organização com a LGPD, proporcionando segurança e
                confiança em relação ao tratamento de dados pessoais."
          />
          <Box
            display="flex"
            gridGap={theme.spacing(4)}
            justifyContent="center"
          >
            <FatButton
              startIcon={<SendIcon />}
              selected={isMine}
              onClick={() => setSelectedItem('mine')}
              className={classes.fatButton}
            >
              Minhas solicitações
            </FatButton>
            <FatButton
              startIcon={<MailIcon />}
              selected={isAll}
              onClick={() => setSelectedItem('all')}
              className={classes.fatButton}
            >
              Todas as solicitações
            </FatButton>
          </Box>
          <Paper variant="outlined">
            <TicketTable
              ticketData={response?.data}
              isLoading={isLoading}
              refresh={refresh}
            />
            <Box px={2} display="flex" justifyContent="flex-end">
              {!isLoading && response ? (
                <TablePagination
                  component="div"
                  count={response?.data?.meta?.totalCount || 0}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  page={page - 1}
                  rowsPerPage={perPage}
                  rowsPerPageOptions={[5, 10, 25, 100]}
                  labelRowsPerPage={isDesktop ? 'Por página' : ''}
                  nextIconButtonProps={{ size: 'small' }}
                  backIconButtonProps={{ size: 'small' }}
                />
              ) : (
                <Box py="11px">
                  <Skeleton variant="rect" width={200} height={30} />
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
        <Filters filter={filter} alphabeticalOrder>
          <input textfieldinput="true" label="Identificador" name="id" />
          <select textfieldinput="true" label="Categoria" name="title">
            <option value=""></option>
            {technicalFaqsConstants.TICKET_SUBJECT.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <select textfieldinput="true" label="Status" name="statusId">
            <option value=""></option>
            <option value={ticketConstants.NEW_STATUS_ID}>Novo</option>
            <option value={ticketConstants.PENDING_STATUS_ID}>Pendente</option>
            <option value={ticketConstants.COMPLETED_STATUS_ID}>
              Concluído
            </option>
            <option value={ticketConstants.CANCELED_STATUS_ID}>
              Cancelado
            </option>
            <option value={ticketConstants.AWAITING_CONFIRMATION_STATUS_ID}>
              Aguardando Confirmação
            </option>
          </select>
          <input textfieldinput="true" label="Título" name="subtitle" />

          <input
            datepickerinput="true"
            label="Data de criação inicial"
            name="createdAtGt"
            data-target="createdAtLt"
            data-min
          />

          <input
            datepickerinput="true"
            label="Data de criação final"
            name="createdAtLt"
            data-target="createdAtGt"
            data-max
          />
          <input
            datepickerinput="true"
            label="Ultima interação inicial"
            name="updatedAtGt"
            data-target="updatedAtLt"
            data-min
          />
          <input
            datepickerinput="true"
            label="Ultima interação final"
            name="updatedAtLt"
            data-target="updatedAtGt"
            data-max
          />
        </Filters>
      </Container>
      <DriveTour
        stepsMatrix={
          technicalFaqsConstants.TECHNICAL_FAQS_TICKTES_DRIVER_STEPS_OBJECT
        }
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default Tickets
